import React, { useCallback, useEffect } from "react";

import { LoadingIcon } from "@packages/ds";
import { Command } from "cmdk";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";

import getUrl from "Libs/getUrl";
import { copyToClipboard } from "Libs/utils";
import { organizationSelector } from "Reducers/organization";
import {
  loadProjectCapability,
  projectCapabilityWithOrganizationIdSelector,
  selectCapabilitiesAreLoading
} from "Reducers/project/capabilities";
import { gitProjectSelector, loadOneProject } from "Reducers/project/project";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import { useCommandMenuContext } from "../hooks";
import * as S from "../styles";

import { Highlighter } from "./Highlighter";
import { Item } from "./Item";

type ProjectActionsProps = {
  projectId: string;
  organizationId: string;
  defaultBranch: string;
  hasEditLink: boolean;
  hasManageAccessLink: boolean;
};

export function ProjectActions({
  projectId,
  organizationId,
  defaultBranch,
  hasEditLink,
  hasManageAccessLink
}: ProjectActionsProps) {
  const { organizationId: organizationName } = useParams<{
    organizationId: string;
  }>();
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedOrganization = useAppSelector(state =>
    organizationSelector(state, {
      organizationId
    })
  );
  const { inputValue } = useCommandMenuContext();

  const project = useAppSelector(state =>
    gitProjectSelector(state, {
      projectId,
      organizationId
    })
  );
  const capabilities = useAppSelector(state =>
    projectCapabilityWithOrganizationIdSelector(state, {
      projectId,
      organizationId
    })
  );
  const isLoadingCapabilities = useAppSelector(state =>
    selectCapabilitiesAreLoading(state, {
      projectId,
      organizationId: organizationName!
    })
  );

  useEffect(() => {
    async function loadCapabilities() {
      if (!capabilities && !isLoadingCapabilities) {
        if (!project) {
          await dispatch(
            loadOneProject({
              projectId,
              organizationId,
              loadActivitiesAndSubscribe: false
            })
          );
        }
        dispatch(
          loadProjectCapability({
            organizationId,
            organizationName: organizationName!,
            projectId
          })
        );
      }
    }

    loadCapabilities();
  }, [
    capabilities,
    dispatch,
    isLoadingCapabilities,
    organizationId,
    organizationName,
    project,
    projectId
  ]);

  const canAddCustomDomains = capabilities?.custom_domains?.enabled ?? false;
  const canAddIntegrations = capabilities?.integrations?.enabled ?? false;
  const onCopyClick = useCallback(async () => {
    copyToClipboard(projectId);
  }, [projectId]);

  if (isLoadingCapabilities && !capabilities) {
    return (
      <S.LoaderContainer>
        <Command.Loading>
          <LoadingIcon />
        </Command.Loading>
      </S.LoaderContainer>
    );
  }

  return (
    <>
      <Item
        value={intl.formatMessage({ id: "command_k.copy_project_id" })}
        onSelect={onCopyClick}
        type="copy"
      >
        {intl.formatMessage({ id: "command_k.copy_project_id" })}
      </Item>
      {!inputValue && <S.Separator />}
      {canAddCustomDomains && (
        <Item
          value={intl.formatMessage({ id: "settings.domains.add.new.title" })}
          onSelect={() =>
            navigate(
              getUrl({
                key: "organization.project.settings.domains",
                props: {
                  organizationId: selectedOrganization?.name || "",
                  projectId
                }
              })
            )
          }
        >
          <Highlighter
            searchText={inputValue}
            textToHighlight={intl.formatMessage({
              id: "settings.domains.add.new.title"
            })}
          />
        </Item>
      )}
      {hasManageAccessLink && (
        <Item
          value={intl.formatMessage({ id: "add_variable" })}
          onSelect={() =>
            navigate(
              getUrl({
                key: "organization.project.settings.variables.new",
                props: {
                  organizationId: selectedOrganization?.name || "",
                  projectId
                }
              })
            )
          }
        >
          <Highlighter
            searchText={inputValue}
            textToHighlight={intl.formatMessage({
              id: "add_variable"
            })}
          />
        </Item>
      )}
      {hasEditLink && (
        <Item
          value={intl.formatMessage({ id: "add_certificate" })}
          onSelect={() =>
            navigate(
              getUrl({
                key: "organization.project.settings.certificates.add",
                props: {
                  organizationId: selectedOrganization?.name || "",
                  projectId
                }
              })
            )
          }
        >
          <Highlighter
            searchText={inputValue}
            textToHighlight={intl.formatMessage({
              id: "add_certificate"
            })}
          />
        </Item>
      )}
      {canAddIntegrations && (
        <Item
          value={intl.formatMessage({ id: "integrations.add" })}
          onSelect={() =>
            navigate(
              getUrl({
                key: "organization.project.settings.integrations.setup",
                props: {
                  organizationId: selectedOrganization?.name || "",
                  projectId
                }
              })
            )
          }
        >
          <Highlighter
            searchText={inputValue}
            textToHighlight={intl.formatMessage({
              id: "integrations.add"
            })}
          />
        </Item>
      )}
      {!inputValue && <S.Separator />}
      {hasEditLink && (
        <Item
          value={intl.formatMessage({
            id: "settings.menu.project_settings"
          })}
          onSelect={() =>
            navigate(
              getUrl({
                key: "organization.project.settings",
                props: {
                  organizationId: selectedOrganization?.name || "",
                  projectId
                }
              })
            )
          }
        >
          <Highlighter
            searchText={inputValue}
            textToHighlight={intl.formatMessage({
              id: "settings.menu.project_settings"
            })}
          />
        </Item>
      )}
      {hasManageAccessLink && (
        <Item
          value={intl.formatMessage({
            id: "project_access"
          })}
          onSelect={() =>
            navigate(
              getUrl({
                key: "organization.project.settings.access",
                props: {
                  organizationId: selectedOrganization?.name || "",
                  projectId
                }
              })
            )
          }
        >
          <Highlighter
            searchText={inputValue}
            textToHighlight={intl.formatMessage({
              id: "project_access"
            })}
          />
        </Item>
      )}
      {hasEditLink && (
        <Item
          value={intl.formatMessage({
            id: "project_backups"
          })}
          onSelect={() =>
            navigate(
              getUrl({
                key: "organization.project.environment.backups",
                props: {
                  organizationId: selectedOrganization?.name || "",
                  projectId,
                  environmentId: defaultBranch
                }
              })
            )
          }
        >
          <Highlighter
            searchText={inputValue}
            textToHighlight={intl.formatMessage({
              id: "project_backups"
            })}
          />
        </Item>
      )}
    </>
  );
}
